/** The adventure-map Presenter: the contract in `docs/specs/presentation.md`.
 *
 *   mount(canvas)            build the renderer and load the atlas
 *   update(view)             adopt a PlayerObservation
 *   animate(events, signal)  play what the events imply, then resolve
 *   dispose()
 *
 * The renderer is a client of the simulation and decides nothing (I10). It
 * reads `PlayerObservation` and `GameEvent`; it has no import path to a rule
 * module, and every type it names comes from the kernel's barrel.
 *
 * Frames are drawn on demand, not on a metronome. A redraw happens when the
 * observation changed, when the camera moved, while something is animating, or
 * when the idle cycle ticks over to its next frame - which is roughly four
 * times a second, not sixty. That is what makes the budget in the spec ("must
 * not redraw at all when version and the camera are unchanged") true rather
 * than aspirational.
 */

import { Application, Container } from "pixi.js";
import type { Renderer } from "pixi.js";
import type {
  GameEvent, ObservedHero, ObservedObject, PlayerObservation,
} from "@game/core";
import { Atlas } from "../atlas.js";
import type { CameraState, CellPoint, PixelPoint, SnappedCamera } from "../coords.js";
import {
  TILE_SIZE, cameraWorldRect, cellGround, cellPoint, clampCameraToMap, clampZoom,
  screenToCell, snapCamera, visibleCellRect,
} from "../coords.js";
import { FogLayer, SHROUD_RGB } from "./fog.js";
import type { PixelRect } from "./frames.js";
import { HeroDirector } from "./hero.js";
import type { HoverPath, ReachableLike } from "./highlight.js";
import { HighlightLayer } from "./highlight.js";
import { objectKeyCandidates, playerTint, townObjectKey } from "./keys.js";
import type { WorldSprite } from "./objects.js";
import {
  LAYER_BOAT, LAYER_FLAT, LAYER_OBJECT, LAYER_TOWN, ObjectLayer, footprintGroundX,
  makeWorldSprite, resolveOwned, spriteTilesWide,
} from "./objects.js";
import { ShadowLayer } from "./shadows.js";
import { TerrainLayer } from "./terrain.js";
import type { TerrainPlanes } from "./terrain.js";
import { measureDeviceScale } from "../text/devicePixels.js";

/** The only thing the world renderer is given. */
export interface WorldView {
  readonly obs: PlayerObservation;
  readonly events: readonly GameEvent[];
  readonly version: number;
}

export interface Presenter {
  mount(canvas: HTMLCanvasElement): Promise<void>;
  update(view: WorldView): void;
  animate(events: readonly GameEvent[], signal: AbortSignal): Promise<void>;
  dispose(): void;
}

export interface PresenterOptions {
  readonly assetsBaseUrl?: string;
  /**
   * An atlas to share rather than load. The app hands the same `Atlas` to the
   * battle screen and to this presenter: a second `Atlas.load` was a second
   * gigabyte of decoded sheets in the renderer process (see `atlas.ts`). A
   * shared atlas is not destroyed by `dispose`; its owner does that.
   */
  readonly atlas?: Atlas | Promise<Atlas>;
  readonly backgroundColor?: number;
  readonly maxResidentChunks?: number;
  readonly decals?: boolean;
  /** Milliseconds a hero takes to cross one cell. */
  readonly stepMs?: number;
  /**
   * Section 31 - what each magic ground is washed in, by ground id.
   *
   * Handed in by the host rather than read out of a registry here: the
   * renderer may reach the kernel's types but not its content, and what a
   * ground looks like is authored beside what it does (`MagicGroundDef.tint`).
   * Absent, magic ground draws nothing and the map is the map it always was.
   */
  readonly groundWash?: Readonly<Record<string, { readonly colour: number; readonly alpha: number }>>;
}

/**
 * The pack groups the adventure map draws from. Measured with the
 * `touchedSheets` probe over the adventure scenes (`qa/`); a group missing here
 * fails loudly in `Atlas.texture` rather than drawing nothing.
 */
const WORLD_SHEET_GROUPS: readonly string[] = ["objects", "terrain"];

const IDLE_TICK_MS = 260;
const PICKUP_MS = 420;
const FOG_REVEAL_MS = 340;

export class AdventurePresenter implements Presenter {
  private app: Application | null = null;
  private atlas: Atlas | null = null;
  private ownsAtlas = false;

  private readonly world = new Container({ label: "world" });
  private terrain: TerrainLayer | null = null;
  private heroes: HeroDirector | null = null;
  private readonly highlight = new HighlightLayer();

  /** Public so a host can toggle a layer for a screenshot comparison (I15).
   *  Nothing in the game loop reads them from outside. */
  objects: ObjectLayer | null = null;
  readonly shadows = new ShadowLayer();
  readonly fog = new FogLayer();

  private obs: PlayerObservation | null = null;
  private planes: TerrainPlanes | null = null;
  private prevTerrain: Uint8Array | null = null;
  private prevVisibility: Uint8Array | null = null;
  private staticSprites: readonly WorldSprite[] = [];
  private readonly pickups = new Map<string, number>();

  private camera: CameraState = { x: 0, y: 0, zoom: 1, viewW: 1366, viewH: 768, dpr: 1 };
  private level = 0;
  private followed: string | null = null;

  private dirty = true;
  private animationsOn = true;
  private clock = 0;
  private lastIdleTick = -1;
  private lastFrameTime = 0;
  private raf = 0;
  private disposed = false;

  constructor(private readonly opts: PresenterOptions = {}) {}

  // ------------------------------------------------------------ lifecycle

  async mount(canvas: HTMLCanvasElement): Promise<void> {
    const app = new Application();
    // Measured off the canvas rather than read off `devicePixelRatio`, so a
    // scaled ancestor can never leave the backing store a different size from
    // the glass (see `text/devicePixels.ts`). Browser zoom is honoured: at
    // 80% this is 0.8, and `clampZoom` picks the camera zoom that still puts
    // a whole device pixel on every world pixel.
    const dpr = measureDeviceScale(canvas);
    await app.init({
      canvas,
      width: canvas.clientWidth || 1366,
      height: canvas.clientHeight || 768,
      resolution: dpr,
      autoDensity: true,
      antialias: false,
      // Pixel art has no business being resampled by the compositor either.
      // The same navy the shroud is painted in (`SHROUD_RGB`), so that the
      // world past the fog texture's one-cell border is the same nothing as
      // the world inside it. It was 0x0a0d12, which is LIGHTER than the
      // shroud: the map's own edge showed as a faint rectangle around the
      // unexplored ground, which is the one place a player must not be able to
      // read the map's dimensions from.
      backgroundColor: this.opts.backgroundColor
        ?? ((SHROUD_RGB.r << 16) | (SHROUD_RGB.g << 8) | SHROUD_RGB.b),
      powerPreference: "high-performance",
    });
    // The scene is submitted by hand, from `frame()`; a free-running ticker
    // would redraw sixty times a second for a map that has not changed.
    app.stop();
    this.app = app;

    if (this.opts.atlas !== undefined) {
      this.atlas = await this.opts.atlas;
    } else {
      this.atlas = await Atlas.load({ baseUrl: this.opts.assetsBaseUrl ?? "/assets" });
      this.ownsAtlas = true;
    }
    const renderer: Renderer = app.renderer;
    // The map's sheet groups into this context now; the CPU copies this
    // context will never read again are dropped. See `Atlas.prepare`.
    await this.atlas.prepare(renderer, WORLD_SHEET_GROUPS);

    const terrainOpts: { maxResidentChunks?: number; decals?: boolean } = {};
    if (this.opts.maxResidentChunks !== undefined) {
      terrainOpts.maxResidentChunks = this.opts.maxResidentChunks;
    }
    if (this.opts.decals !== undefined) terrainOpts.decals = this.opts.decals;

    this.terrain = new TerrainLayer(this.atlas, renderer, terrainOpts);
    this.objects = new ObjectLayer(this.atlas);
    this.heroes = new HeroDirector(this.atlas);

    this.world.addChild(this.terrain.container);
    this.world.addChild(this.highlight.ground);
    this.world.addChild(this.shadows.container);
    this.world.addChild(this.objects.container);
    this.world.addChild(this.highlight.overlay);
    this.world.addChild(this.fog.container);
    app.stage.addChild(this.world);

    // `renderer.screen` is the CSS/logical size. `renderer.width` is the same
    // number (texture frame width, not the backing-store pixelWidth). Dividing
    // it by dpr a second time made viewW/viewH a quarter of the glass on a
    // 2x phone: terrain chunks are 32 cells and still filled the window, but
    // heroes and pickups were culled to the top-left quadrant.
    this.camera = {
      ...this.camera,
      viewW: app.renderer.screen.width,
      viewH: app.renderer.screen.height,
      dpr,
    };

    this.dirty = true;
    this.raf = requestAnimationFrame(this.frame);
    if (this.obs !== null) this.adopt(this.obs);
  }

  dispose(): void {
    this.disposed = true;
    if (this.raf !== 0) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.objects?.destroy();
    this.terrain?.destroy();
    this.shadows.destroy();
    this.fog.destroy();
    this.highlight.destroy();
    this.world.destroy({ children: false });
    if (this.ownsAtlas) this.atlas?.destroy();
    this.app?.destroy(false, { children: true, context: true });
    this.app = null;
    this.atlas = null;
  }

  // ------------------------------------------------------------ the contract

  update(view: WorldView): void {
    this.obs = view.obs;
    if (this.app === null) return;
    this.adopt(view.obs);
  }

  async animate(events: readonly GameEvent[], signal: AbortSignal): Promise<void> {
    if (!this.animationsOn || this.heroes === null) return;
    for (const seg of segmentEvents(events)) {
      if (signal.aborted) break;
      if (seg.kind === "move") {
        const walked = this.heroes.startWalk(seg.hero, seg.path, this.opts.stepMs);
        this.followed = seg.hero;
        this.dirty = true;
        await Promise.race([walked, aborted(signal)]);
      } else if (seg.kind === "pickup") {
        this.pickups.set(seg.object, 0);
        this.dirty = true;
        await wait(PICKUP_MS, signal);
        this.pickups.delete(seg.object);
        this.dirty = true;
      } else {
        await wait(FOG_REVEAL_MS, signal);
      }
    }
    if (signal.aborted) this.settle();
  }

  // ------------------------------------------------------------ camera

  setCamera(next: Partial<CameraState>): void {
    this.camera = clampCameraToMap(
      { ...this.camera, ...next },
      this.obs?.map.width ?? 1, this.obs?.map.height ?? 1,
    );
    this.dirty = true;
  }

  panBy(dxScreen: number, dyScreen: number): void {
    this.setCamera({
      x: this.camera.x + dxScreen / this.camera.zoom,
      y: this.camera.y + dyScreen / this.camera.zoom,
    });
  }

  /**
   * Ask for a magnification. What is used is `clampZoom` of it - one of
   * `ZOOM_OUT_STEPS`, or a whole number of device pixels per world pixel -
   * and `zoom()` says
   * which; a caller laying out cells against the view reads that back rather
   * than assuming its ask was honoured.
   */
  setZoom(zoom: number): void { this.setCamera({ zoom: clampZoom(zoom, this.camera.dpr) }); }

  /** The magnification actually in force, in CSS pixels per world pixel. */
  zoom(): number { return this.snapped().zoom; }

  centreOn(cell: CellPoint): void {
    const g = cellGround(cell);
    this.setCamera({ x: g.x as number, y: (g.y as number) - TILE_SIZE / 2 });
  }

  resize(cssWidth: number, cssHeight: number): void {
    // A browser zoom change arrives as a resize and moves the device ratio;
    // the backing store and the camera's snap grid follow it.
    const dpr = this.app === null ? this.camera.dpr : measureDeviceScale(this.app.canvas);
    this.app?.renderer.resize(cssWidth, cssHeight, dpr);
    this.setCamera({ viewW: cssWidth, viewH: cssHeight, dpr });
  }

  setLevel(z: number): void {
    if (this.level === z) return;
    this.level = z;
    this.terrain?.invalidateAll();
    if (this.obs !== null) this.adopt(this.obs);
  }

  /** The cell under a pointer position in CSS pixels relative to the canvas. */
  pickCell(screen: PixelPoint): CellPoint {
    return screenToCell(screen, this.snapped(), this.level);
  }

  setFollowedHero(heroId: string | null): void { this.followed = heroId; }

  // ------------------------------------------------------------ overlays

  setReachable(reach: ReachableLike | null, budget: number): void {
    this.highlight.setReach(reach, budget);
    this.dirty = true;
  }

  setHoverPath(path: HoverPath | null): void {
    this.highlight.setPath(path);
    this.dirty = true;
  }

  // ------------------------------------------------------------ debug (I15)

  /** Freeze every animation at its resting frame. Required for the L6 harness. */
  disableAnimations(): void {
    this.animationsOn = false;
    this.settle();
  }

  enableAnimations(): void { this.animationsOn = true; }

  /** Resolves when nothing is left to draw. */
  async settled(): Promise<void> {
    this.settle();
    this.render();
    await Promise.resolve();
  }

  private settle(): void {
    this.heroes?.settle();
    this.fog.settle();
    this.pickups.clear();
    this.dirty = true;
  }

  // ------------------------------------------------------------ internals

  private snapped(): SnappedCamera { return snapCamera(this.camera); }

  /**
   * Nearest sampling while a world pixel is a whole number of device pixels;
   * linear while the frame is minified (`ZOOM_OUT_STEPS`: a half, where a
   * linear sample at the snapped origin is an exact 2x2 box filter, or three
   * quarters, a soft 4:3 blend - the argument is in `coords.ts`). Re-applied on
   * every render rather than on change, because the atlas is a shared object
   * and another presenter may have set its sheets back; each layer skips the
   * sources already in the asked-for mode, so a steady state costs a loop
   * over a few dozen sources and no GPU work.
   */
  private applySampling(cam: SnappedCamera): void {
    const mode = cam.zoom * cam.dpr < 1 - 1e-9 ? "linear" : "nearest";
    this.atlas?.setScaleMode(mode);
    this.terrain?.setScaleMode(mode);
    this.fog.setScaleMode(mode);
  }

  /**
   * The colour each ground slot is washed in, IN THE MAP'S OWN SLOT ORDER.
   *
   * The renderer is handed a plane of slot numbers and has to turn each into a
   * colour; the legend that joins them is the map's (`groundIndex`), and what a
   * ground looks like is the host's table. A slot naming a ground the host does
   * not know washes nothing - the same answer the kernel gives it in `groundAt`
   * - so the picture and the rules agree about a map that outlived its content.
   */
  private washOf(index: readonly (string | null)[]): TerrainPlanes["wash"] {
    const table = this.opts.groundWash;
    if (table === undefined) return undefined;
    return index.map((id) => {
      const look = id === null ? undefined : table[id];
      return look === undefined || id === null ? null : { id, ...look };
    });
  }

  private adopt(obs: PlayerObservation): void {
    const m = obs.map;
    const planes: TerrainPlanes = {
      width: m.width, height: m.height, levels: m.levels,
      terrain: m.terrain, road: m.road, river: m.river, visibility: m.visibility,
      ground: m.ground,
      wash: this.washOf(m.groundIndex),
    };

    const first = this.planes === null
      || this.planes.width !== m.width || this.planes.height !== m.height;
    this.planes = planes;
    if (first) {
      this.terrain?.setPlanes(planes);
      this.prevTerrain = Uint8Array.from(planes.terrain);
      this.prevVisibility = Uint8Array.from(planes.visibility);
      this.camera = clampCameraToMap(this.camera, m.width, m.height);
    } else {
      // Diff rather than re-bake: a fog reveal changes a disc of cells, and
      // re-baking every resident chunk for that would stutter every step.
      this.diffTerrain(planes);
      this.terrain?.refreshPlanes(planes);
    }

    this.fog.setPlane(
      { width: m.width, height: m.height, visibility: m.visibility },
      this.level, first,
    );
    this.highlight.setMap(m.width, m.height, this.level);
    this.highlight.setVisibility(m.visibility);

    const colours = colourIndex(obs);
    this.highlight.setSelectionColour(playerTint(obs.self?.colour ?? null));
    this.heroes?.sync(
      obs.heroes.filter((h) => h.z === this.level).map(heroSnapshot),
      (owner) => colours.get(owner) ?? null,
    );
    this.staticSprites = this.buildStatics(obs, colours);
    this.dirty = true;
  }

  private diffTerrain(next: TerrainPlanes): void {
    const prevT = this.prevTerrain;
    const prevV = this.prevVisibility;
    this.prevTerrain = Uint8Array.from(next.terrain);
    this.prevVisibility = Uint8Array.from(next.visibility);
    if (prevT === null || prevV === null || prevT.length !== next.terrain.length) {
      this.terrain?.invalidateAll();
      return;
    }
    const plane = next.width * next.height;
    for (let z = 0; z < next.levels; z++) {
      const base = plane * z;
      for (let i = 0; i < plane; i++) {
        const j = base + i;
        // The bake depends on the terrain byte and on whether the cell has
        // ever been seen (unexplored ground carries a zeroed terrain byte and
        // gets no decals). It does NOT depend on remembered-vs-visible, which
        // the fog layer owns - and which changes on every step of every hero.
        // Invalidating on that would re-bake four 2048px chunks per step.
        if (prevT[j] === next.terrain[j]
          && (prevV[j] === 0) === (next.visibility[j] === 0)) continue;
        this.terrain?.invalidateCell(i % next.width, Math.floor(i / next.width), z);
      }
    }
  }

  /**
   * Every sprite that is not a hero, for this level.
   *
   * A town appears in the observation TWICE - once in `obs.towns`, which
   * carries the name and the owner and is anchored on the town's *entrance*
   * cell, and once in `obs.objects` as a `town`-kind `MapObjectState` anchored
   * on the bottom-left of its footprint. They are the same building. Drawing
   * both put two castles on the map three tiles apart, and that - not the size
   * of the art - is what made the adventure map read as a diorama.
   *
   * The `obs.towns` entry is the one that draws, because the art is registered
   * to the gate: the packer's `drawOffsetPx` for every town sheet puts the
   * designed doorway exactly half a tile right of the sprite's left edge plus
   * its anchor, which is `cellGround` of the entrance cell. Anchoring on the
   * object's footprint origin instead slides the gate a tile west of the road
   * the generator laid to it. Its owner rule is also the kinder one: your own
   * town keeps its colours when it falls out of sight.
   */
  private buildStatics(
    obs: PlayerObservation, colours: ReadonlyMap<string, string>,
  ): WorldSprite[] {
    const atlas = this.atlas;
    if (atlas === null) return [];
    const out: WorldSprite[] = [];

    const townCells = new Set<string>();
    for (const t of obs.towns) {
      if (t.z !== this.level) continue;
      townCells.add(cellKey(t.x, t.y));
    }

    for (const o of obs.objects) {
      if (o.z !== this.level) continue;
      const key = atlas.resolve(objectKeyCandidates(o.def, o.kind));
      if (key === null) continue;
      const frame = atlas.frame(key);
      const tiles = frame === null ? 1 : spriteTilesWide(frame);

      // A town-kind object whose front row carries a TownState is that town,
      // and `obs.towns` draws it. Anything else - a neutral town a scenario
      // placed with no TownState behind it - still draws from here.
      if (o.kind === "town") {
        let claimed = false;
        for (let dx = 0; dx < tiles && !claimed; dx++) {
          claimed = townCells.has(cellKey(o.x + dx, o.y));
        }
        if (claimed) continue;
      }

      const owned = resolveOwned(atlas, key, o.owner === null ? null : colours.get(o.owner as string) ?? null);
      const g = cellGround(cellPoint(o.x, o.y, o.z));
      const lift = this.pickups.get(o.id as string);
      const s = makeWorldSprite(atlas, {
        id: `obj:${o.id}`,
        key: owned.key,
        groundX: footprintGroundX(o.x, tiles),
        groundY: (g.y as number) - (lift ?? 0) * 22,
        layer: layerFor(o),
        alpha: lift === undefined ? 1 : 1 - lift,
        flagKey: owned.flagKey,
        flagTint: owned.flagTint,
      });
      if (s !== null) out.push(s);
    }

    for (const t of obs.towns) {
      if (t.z !== this.level) continue;
      const base = townObjectKey(t.faction);
      if (!atlas.has(base)) continue;
      const owned = resolveOwned(atlas, base, t.owner === null ? null : colours.get(t.owner as string) ?? null);
      const g = cellGround(cellPoint(t.x, t.y, t.z));
      const s = makeWorldSprite(atlas, {
        id: `town:${t.id}`,
        key: owned.key,
        groundX: g.x as number,
        groundY: g.y as number,
        layer: LAYER_TOWN,
        flagKey: owned.flagKey,
        flagTint: owned.flagTint,
      });
      if (s !== null) out.push(s);
    }
    return out;
  }

  private readonly frame = (t: number): void => {
    if (this.disposed) return;
    this.raf = requestAnimationFrame(this.frame);
    const dt = this.lastFrameTime === 0 ? 16 : Math.min(100, t - this.lastFrameTime);
    this.lastFrameTime = t;

    let moving = false;
    if (this.animationsOn) {
      this.clock += dt;
      this.heroes?.advance(dt);
      if (this.heroes?.walking === true) moving = true;
      if (this.fog.advance(dt)) moving = true;
      if (this.pickups.size > 0) {
        for (const [id, v] of this.pickups) this.pickups.set(id, Math.min(1, v + dt / PICKUP_MS));
        moving = true;
      }
      const tick = Math.floor(this.clock / IDLE_TICK_MS);
      if (tick !== this.lastIdleTick) {
        this.lastIdleTick = tick;
        moving = true;
      }
    }

    if (moving && this.followed !== null && this.heroes !== null) {
      const c = this.heroes.centreOf(this.followed);
      if (c !== null) this.setCamera({ x: c.x, y: c.y });
    }

    if (!this.dirty && !moving) return;
    this.render();
  };

  private render(): void {
    const app = this.app;
    const obs = this.obs;
    if (app === null || obs === null) return;

    const cam = this.snapped();
    this.applySampling(cam);
    this.world.scale.set(cam.zoom);
    // Whole device pixels, because `snapCamera` already forced
    // `originX * zoom * dpr` to be an integer.
    this.world.position.set(-cam.originX * cam.zoom, -cam.originY * cam.zoom);

    this.terrain?.update(cam, this.level);
    this.highlight.update(visibleCellRect(cam, obs.map.width, obs.map.height, 1));
    // The shroud's starfield is per-cell sprites, so it is built for the view
    // rather than for the map; two cells of pad covers the border ring and the
    // cell a star tile can hang off the edge of.
    this.fog.setView(visibleCellRect(cam, obs.map.width, obs.map.height, 2));

    const w = cameraWorldRect(cam);
    const view: PixelRect = { x: w.x0, y: w.y0, w: w.x1 - w.x0, h: w.y1 - w.y0 };
    const live = this.pickups.size > 0
      ? this.buildStatics(obs, colourIndex(obs))
      : this.staticSprites;
    const heroSprites = this.heroes?.sprites(this.level, this.animationsOn) ?? [];
    this.objects?.sync([...live, ...heroSprites], view, this.shadows);

    app.renderer.render(app.stage);
    this.dirty = false;
  }
}

export const createAdventurePresenter = (opts?: PresenterOptions): AdventurePresenter =>
  new AdventurePresenter(opts);

// ------------------------------------------------------------------- helpers

/** Cell key for the town/object join, within one level. */
const cellKey = (x: number, y: number): string => `${x},${y}`;

const layerFor = (o: ObservedObject): number => {
  if (o.kind === "town") return LAYER_TOWN;
  if (o.kind === "boat") return LAYER_BOAT;
  if (o.kind === "resource" || o.kind === "artifact" || o.kind === "treasure") return LAYER_FLAT;
  return LAYER_OBJECT;
};

const heroSnapshot = (h: ObservedHero): {
  id: string; def: string; owner: string; x: number; y: number; z: number; facing: number;
} => ({
  id: h.id as string,
  def: h.def,
  owner: h.owner as string,
  x: h.x, y: h.y, z: h.z, facing: h.facing,
});

/** Player id -> colour name, for both the viewer and everyone they can see. */
function colourIndex(obs: PlayerObservation): ReadonlyMap<string, string> {
  const m = new Map<string, string>();
  if (obs.self !== null) m.set(obs.self.id as string, obs.self.colour);
  for (const o of obs.opponents) m.set(o.id as string, o.colour);
  return m;
}

type Segment =
  | { readonly kind: "move"; readonly hero: string; readonly path: { x: number; y: number; z: number }[] }
  | { readonly kind: "pickup"; readonly object: string }
  | { readonly kind: "reveal" };

/**
 * Fold the event stream into things worth animating.
 *
 * Consecutive `HeroMoved` events for one hero are one walk, not twenty hops:
 * the kernel emits a step per cell, and playing each as its own animation would
 * make a six-cell move take six times as long as it should.
 */
export function segmentEvents(events: readonly GameEvent[]): Segment[] {
  const out: Segment[] = [];
  let walk: { hero: string; path: { x: number; y: number; z: number }[] } | null = null;

  const flush = (): void => {
    if (walk !== null && walk.path.length > 1) out.push({ kind: "move", ...walk });
    walk = null;
  };

  for (const e of events) {
    if (e.type === "HeroMoved") {
      const hero = e.hero as string;
      if (walk === null || walk.hero !== hero) {
        flush();
        walk = { hero, path: [{ x: e.from[0], y: e.from[1], z: e.from[2] }] };
      }
      walk.path.push({ x: e.to[0], y: e.to[1], z: e.to[2] });
      continue;
    }
    if (e.type === "ObjectVisited") {
      flush();
      out.push({ kind: "pickup", object: e.object as string });
      continue;
    }
    if (e.type === "FogRevealed") {
      // A reveal that arrives mid-walk is part of the walk, not a pause.
      if (walk === null) out.push({ kind: "reveal" });
      continue;
    }
  }
  flush();
  return out;
}

/** Resolves when the signal aborts, and never otherwise. */
function aborted(signal: AbortSignal): Promise<void> {
  if (signal.aborted) return Promise.resolve();
  return new Promise<void>((resolve) => {
    signal.addEventListener("abort", () => { resolve(); }, { once: true });
  });
}

function wait(ms: number, signal: AbortSignal): Promise<void> {
  if (ms <= 0 || signal.aborted) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const done = (): void => {
      clearTimeout(timer);
      signal.removeEventListener("abort", done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    signal.addEventListener("abort", done, { once: true });
  });
}
